/**
 * Выбор: чекбокс, радиогруппа, переключатель, сегментированный контрол.
 *
 * Все четыре — на Radix: доступность паттернов (роли, стрелки, `aria-checked`)
 * стоит слишком дорого, чтобы писать её заново в каждом продукте.
 */

import * as React from "react";
import * as RadixCheckbox from "@radix-ui/react-checkbox";
import * as RadixRadio from "@radix-ui/react-radio-group";
import * as RadixSwitch from "@radix-ui/react-switch";
import { Check, Minus } from "lucide-react";

import { cn } from "@/shared/lib/cn";

const CONTROL_RING =
  "focus-visible:outline-none focus-visible:shadow-[var(--pt-ring)] " +
  "disabled:cursor-not-allowed disabled:opacity-60";

export interface CheckboxProps extends React.ComponentPropsWithoutRef<typeof RadixCheckbox.Root> {
  label?: React.ReactNode;
  hint?: string;
}

export const Checkbox = React.forwardRef<
  React.ComponentRef<typeof RadixCheckbox.Root>,
  CheckboxProps
>(function Checkbox({ className, label, hint, id, ...props }, ref) {
  const generatedId = React.useId();
  const fieldId = id ?? generatedId;

  const control = (
    <RadixCheckbox.Root
      ref={ref}
      id={fieldId}
      className={cn(
        "flex size-[16px] shrink-0 items-center justify-center rounded-[var(--pt-radius-2)]",
        "border border-[var(--pt-border-control)] bg-[var(--pt-bg-surface)]",
        "transition-colors duration-[var(--pt-dur-micro)]",
        "hover:border-[var(--pt-border-strong)]",
        "data-[state=checked]:border-[var(--pt-accent)] data-[state=checked]:bg-[var(--pt-accent)]",
        "data-[state=indeterminate]:border-[var(--pt-accent)] data-[state=indeterminate]:bg-[var(--pt-accent)]",
        CONTROL_RING,
        className,
      )}
      {...props}
    >
      <RadixCheckbox.Indicator className="text-[var(--pt-text-on-accent)]">
        {props.checked === "indeterminate" ? (
          <Minus className="size-[12px]" />
        ) : (
          <Check className="size-[12px]" />
        )}
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  );

  if (!label) {
    return control;
  }

  return (
    <div className="flex items-start gap-[var(--pt-space-8)]">
      {control}
      <div className="min-w-0">
        <label
          htmlFor={fieldId}
          className="cursor-pointer text-[var(--pt-font-ui)] leading-[var(--pt-lh-ui)] text-[var(--pt-text-primary)]"
        >
          {label}
        </label>
        {hint && (
          <p className="text-[var(--pt-fs-xs)] leading-[var(--pt-lh-xs)] text-[var(--pt-text-tertiary)]">
            {hint}
          </p>
        )}
      </div>
    </div>
  );
});

export interface RadioOption {
  value: string;
  label: React.ReactNode;
  hint?: string;
  disabled?: boolean;
}

export interface RadioGroupProps extends React.ComponentPropsWithoutRef<typeof RadixRadio.Root> {
  options: readonly RadioOption[];
}

export const RadioGroup = React.forwardRef<
  React.ComponentRef<typeof RadixRadio.Root>,
  RadioGroupProps
>(function RadioGroup({ options, className, ...props }, ref) {
  return (
    <RadixRadio.Root
      ref={ref}
      className={cn("flex flex-col gap-[var(--pt-space-8)]", className)}
      {...props}
    >
      {options.map((option) => (
        <div key={option.value} className="flex items-start gap-[var(--pt-space-8)]">
          <RadixRadio.Item
            id={`${props.name ?? "radio"}-${option.value}`}
            value={option.value}
            disabled={option.disabled}
            className={cn(
              "flex size-[16px] shrink-0 items-center justify-center rounded-[var(--pt-radius-full)]",
              "border border-[var(--pt-border-control)] bg-[var(--pt-bg-surface)]",
              "hover:border-[var(--pt-border-strong)]",
              "data-[state=checked]:border-[var(--pt-accent)]",
              CONTROL_RING,
            )}
          >
            <RadixRadio.Indicator className="size-[8px] rounded-[var(--pt-radius-full)] bg-[var(--pt-accent)]" />
          </RadixRadio.Item>
          <div className="min-w-0">
            <label
              htmlFor={`${props.name ?? "radio"}-${option.value}`}
              className="cursor-pointer text-[var(--pt-font-ui)] leading-[var(--pt-lh-ui)] text-[var(--pt-text-primary)]"
            >
              {option.label}
            </label>
            {option.hint && (
              <p className="text-[var(--pt-fs-xs)] leading-[var(--pt-lh-xs)] text-[var(--pt-text-tertiary)]">
                {option.hint}
              </p>
            )}
          </div>
        </div>
      ))}
    </RadixRadio.Root>
  );
});

export interface SwitchProps extends React.ComponentPropsWithoutRef<typeof RadixSwitch.Root> {
  label?: React.ReactNode;
}

export const Switch = React.forwardRef<React.ComponentRef<typeof RadixSwitch.Root>, SwitchProps>(
  function Switch({ className, label, id, ...props }, ref) {
    const generatedId = React.useId();
    const fieldId = id ?? generatedId;

    const control = (
      <RadixSwitch.Root
        ref={ref}
        id={fieldId}
        className={cn(
          "relative h-[16px] w-[28px] shrink-0 rounded-[var(--pt-radius-full)]",
          "bg-[var(--pt-bg-active)] transition-colors duration-[var(--pt-dur-micro)]",
          "data-[state=checked]:bg-[var(--pt-accent)]",
          CONTROL_RING,
          className,
        )}
        {...props}
      >
        <RadixSwitch.Thumb
          className={cn(
            "block size-[12px] translate-x-[2px] rounded-[var(--pt-radius-full)]",
            "bg-[var(--pt-bg-surface)] shadow-[var(--pt-shadow-1)]",
            "transition-transform duration-[var(--pt-dur-micro)] ease-[var(--pt-ease-standard)]",
            "data-[state=checked]:translate-x-[14px]",
          )}
        />
      </RadixSwitch.Root>
    );

    if (!label) {
      return control;
    }

    return (
      <div className="flex items-center gap-[var(--pt-space-8)]">
        {control}
        <label
          htmlFor={fieldId}
          className="cursor-pointer text-[var(--pt-font-ui)] leading-[var(--pt-lh-ui)] text-[var(--pt-text-primary)]"
        >
          {label}
        </label>
      </div>
    );
  },
);

export interface SegmentedOption<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Название для скринридера, когда в кнопке только иконка. */
  ariaLabel?: string;
}

export interface SegmentedControlProps<T extends string> {
  options: readonly SegmentedOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
  label: string;
  className?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onValueChange,
  label,
  className,
}: SegmentedControlProps<T>): React.ReactElement {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        "inline-flex h-[var(--pt-control-h-md)] items-center gap-[1px] rounded-[var(--pt-radius-3)]",
        "border border-[var(--pt-border-control)] bg-[var(--pt-bg-sunken)] p-[1px]",
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={option.ariaLabel}
            onClick={() => onValueChange(option.value)}
            className={cn(
              "inline-flex h-full items-center gap-[var(--pt-space-4)] rounded-[calc(var(--pt-radius-3)-1px)]",
              "px-[var(--pt-space-8)] text-[var(--pt-font-ui)] transition-colors",
              "duration-[var(--pt-dur-micro)] focus-visible:outline-none focus-visible:shadow-[var(--pt-ring-inset)]",
              active
                ? "bg-[var(--pt-bg-surface)] text-[var(--pt-text-primary)] shadow-[var(--pt-shadow-1)]"
                : "text-[var(--pt-text-secondary)] hover:text-[var(--pt-text-primary)]",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
